import { useState } from "react";
import { logoSources } from "@/lib/car-make-logos";
import type { PlatformOverview } from "@/lib/analytics";
import type { CarMakeLogoEntry } from "@/lib/car-make-logos";
import { SafeImage } from "@/components/ui/SafeImage";

function MakeLogo({ make, logo, className }: { make: string; logo: CarMakeLogoEntry | null; className?: string }) {
  const sources = logoSources(logo);
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = sources[sourceIndex];

  if (!src) {
    return (
      <div className={className ?? "flex h-16 w-16 items-center justify-center"}>
        <span className="text-sm font-bold text-muted">{make.slice(0, 2).toUpperCase()}</span>
      </div>
    );
  }

  return (
    <SafeImage
      src={src}
      alt={make}
      width={64}
      height={64}
      className={className ?? "h-16 w-16 object-contain"}
      onError={() => {
        if (sourceIndex < sources.length - 1) {
          setSourceIndex((index) => index + 1);
        }
      }}
    />
  );
}

function MakeSquircleCard({
  make,
  modelCount,
  logo,
}: {
  make: string;
  modelCount: number;
  logo: CarMakeLogoEntry | null;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[24px] bg-canvas p-4 text-center transition-colors hover:bg-canvas-hover">
      <MakeLogo make={make} logo={logo} />
      <div className="min-w-0 w-full">
        <p className="truncate text-xs font-semibold text-foreground">{make}</p>
        <p className="mt-1 text-[11px] font-bold text-muted">
          {modelCount} {modelCount === 1 ? "model" : "models"}
        </p>
      </div>
    </div>
  );
}

export { MakeLogo };

export function MakeModelGrid({ platform }: { platform: PlatformOverview }) {
  if (platform.topMakes.length === 0) {
    return <p className="text-sm text-muted">No catalogue makes configured yet.</p>;
  }

  return (
    <div className="stagger-children grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {platform.topMakes.map(({ make, modelCount, logo }) => (
        <MakeSquircleCard key={make} make={make} modelCount={modelCount} logo={logo} />
      ))}
    </div>
  );
}
